import type { Disposable } from "@/shared/contracts/lifecycle";
import { HoverChargeExecutionController } from "@/features/hoverIdle/core/hoverChargeExecutionController";
import { formatOHovRecordDurationMs } from "@/features/hoverIdle/oHov/oHovTimeFormat";
import { getMessages } from "@/i18n/runtime";
import { HoverIntentController } from "@/features/hoverIdle/core/hoverIntentController";
import type { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";
import { HOVER_ANCHOR_TARGET_ID_ATTRIBUTE } from "@/features/hoverIdle/input/hoverPointerInputController";
import xLgIconSource from "bootstrap-icons/icons/x-lg.svg?raw";
import type { HoverIdleSnapshot } from "../types";
import type { WallSnapshot, WallSnapshotEntry } from "./wallSnapshot";
import {
  WALL_DEFINITIONS,
  type WallFirstClearReward,
  type WallId,
} from "./wallDefinitions";
import { DEFAULT_WALL_START_CHARGE_MS as DEFAULT_WALL_ACTION_CHARGE_MS } from "./wallStartCharge";
import { formatWallRewardEffect } from "./wallRewardFormat";

const WALL_ROMAN_NUMERALS = [
  "Ⅰ",
  "Ⅱ",
  "Ⅲ",
  "Ⅳ",
  "Ⅴ",
  "Ⅵ",
  "Ⅶ",
  "Ⅷ",
  "Ⅸ",
] as const;

interface WallStartStopChargeDurationSetting {
  readonly options: readonly number[];
  readonly onChange: (ms: number) => void;
}

interface WallViewOptions {
  readonly container: HTMLElement;
  readonly pointerModel: HoverPointerModel;
  readonly onStartWall: (wallId: WallId) => boolean;
  readonly onStopWall?: (wallId: WallId) => boolean;
  readonly getWallActionChargeDurationMs?: () => number;
  readonly startStopChargeDurationSetting?: WallStartStopChargeDurationSetting;
}

interface WallTargetElements {
  readonly root: HTMLDivElement;
  readonly fill: HTMLDivElement;
  readonly stopButton: HTMLButtonElement;
  readonly nameEl: HTMLHeadingElement;
  readonly constraintEl: HTMLParagraphElement;
  readonly firstClearRewardEl: HTMLParagraphElement;
  readonly rewardEl: HTMLParagraphElement;
  readonly stateEl: HTMLSpanElement;
  readonly bestEl: HTMLSpanElement;
  readonly multiplierRow: HTMLParagraphElement;
  readonly multiplierLabelEl: HTMLSpanElement;
  readonly multiplierEl: HTMLSpanElement;
  startIntent: HoverIntentController | null;
  stopIntent: HoverIntentController | null;
  startChargeController: HoverChargeExecutionController | null;
  stopChargeController: HoverChargeExecutionController | null;
  startBlockedUntilPointerLeaves: boolean;
  stopChargeDecaying: boolean;
  stopChargeDecayFromProgress: number;
}

interface WallTargetViewState {
  readonly active: boolean;
  readonly anyWallActive: boolean;
  readonly challengeAvailable: boolean;
}

export class WallView implements Disposable {
  private readonly root: HTMLDivElement;
  private readonly navItems = new Map<WallId, HTMLButtonElement>();
  private readonly target: WallTargetElements;
  private readonly pointerModel: HoverPointerModel;
  private readonly onStartWall: (wallId: WallId) => boolean;
  private readonly onStopWall: (wallId: WallId) => boolean;
  private readonly getWallActionChargeDurationMs: () => number;
  private readonly viewState = new Map<WallId, WallTargetViewState>();
  private readonly startStopChargeDurationSetting: WallStartStopChargeDurationSetting | null;
  private readonly startStopChargeDurationButtons: HTMLButtonElement[] = [];
  private readonly cleanups: Array<() => void> = [];
  private lastWallSnapshot: WallSnapshot | null = null;
  private selectedWallId: WallId | null = null;

  constructor(options: WallViewOptions) {
    const messages = getMessages();
    this.pointerModel = options.pointerModel;
    this.onStartWall = options.onStartWall;
    this.onStopWall = options.onStopWall ?? (() => false);
    this.getWallActionChargeDurationMs =
      options.getWallActionChargeDurationMs ??
      (() => DEFAULT_WALL_ACTION_CHARGE_MS);
    this.startStopChargeDurationSetting =
      options.startStopChargeDurationSetting ?? null;

    this.root = document.createElement("div");
    this.root.className = "hover-idle-panel hover-idle-panel-wall";
    this.root.setAttribute("role", "region");
    this.root.setAttribute("aria-label", messages.wall.regionAria);

    const layout = document.createElement("div");
    layout.className = "hover-idle-wall-layout";
    this.root.append(layout);

    const settingColumn = document.createElement("div");
    settingColumn.className =
      "hover-idle-game-surface-settings hover-idle-wall-setting-column";
    layout.append(settingColumn);

    const nav = document.createElement("div");
    nav.className = "hover-idle-wall-nav";
    nav.setAttribute("aria-label", messages.wall.title);
    const navColumn = document.createElement("div");
    navColumn.className = "hover-idle-wall-nav-column";
    navColumn.append(nav);
    layout.append(navColumn);

    for (const [index, wallId] of getKnownWallIds().entries()) {
      const navItem = this.createNavItem(wallId, index);
      nav.append(navItem);
      this.navItems.set(wallId, navItem);
    }

    if (this.startStopChargeDurationSetting !== null) {
      const control = this.createStartStopChargeDurationControl(
        this.startStopChargeDurationSetting,
      );
      settingColumn.append(control);
    }

    const cardArea = document.createElement("div");
    cardArea.className = "hover-idle-wall-card-area";
    layout.append(cardArea);

    this.target = this.createTarget();
    cardArea.append(this.target.root);

    options.container.append(this.root);
    this.syncStartStopChargeDurationControl();
  }

  refresh(snapshot: HoverIdleSnapshot): void {
    this.applyWallSnapshot(snapshot.wall);
    this.syncStartStopChargeDurationControl();
  }

  private applyWallSnapshot(wall: WallSnapshot): void {
    this.lastWallSnapshot = wall;
    const anyWallActive = wall.activeWallId !== null;
    this.viewState.clear();
    for (const entry of wall.walls) {
      this.viewState.set(entry.id, {
        active: entry.active,
        anyWallActive,
        challengeAvailable: entry.challengeAvailable,
      });
    }

    this.selectedWallId = this.resolveSelectedWallId(wall);
    this.renderWallSnapshot(wall);
  }

  private resolveSelectedWallId(wall: WallSnapshot): WallId | null {
    const visibleWallIds = getVisibleWallIds(wall);
    if (
      wall.activeWallId !== null &&
      visibleWallIds.includes(wall.activeWallId)
    ) {
      return wall.activeWallId;
    }
    if (
      this.selectedWallId !== null &&
      visibleWallIds.includes(this.selectedWallId)
    ) {
      return this.selectedWallId;
    }
    return visibleWallIds[0] ?? null;
  }

  private renderWallSnapshot(wall: WallSnapshot): void {
    const entriesById = createWallEntryMap(wall);
    this.applyNavSnapshot(wall, entriesById);

    const selectedEntry =
      this.selectedWallId !== null
        ? (entriesById.get(this.selectedWallId) ?? null)
        : null;
    if (selectedEntry === null || !selectedEntry.challengeAvailable) {
      this.hideTarget();
      return;
    }
    this.applyTargetSnapshot(
      this.target,
      selectedEntry,
      wall.activeWallId !== null,
    );
  }

  private applyNavSnapshot(
    wall: WallSnapshot,
    entriesById: ReadonlyMap<WallId, WallSnapshotEntry>,
  ): void {
    for (const wallId of getKnownWallIds()) {
      const navItem = this.navItems.get(wallId);
      if (!navItem) continue;
      const entry = entriesById.get(wallId);
      const visible = entry?.challengeAvailable ?? false;
      const active = entry?.active ?? false;
      const selected = this.selectedWallId === wallId;
      const disabled = visible && wall.activeWallId !== null && !active;

      navItem.hidden = !visible;
      navItem.disabled = !visible || disabled;
      navItem.setAttribute("aria-hidden", visible ? "false" : "true");
      navItem.setAttribute("aria-current", selected ? "true" : "false");
      navItem.setAttribute("aria-disabled", disabled ? "true" : "false");
      navItem.dataset.challengeAvailable = visible ? "true" : "false";
      navItem.dataset.selected = selected ? "true" : "false";
      navItem.dataset.active = active ? "true" : "false";
      navItem.dataset.disabled = disabled ? "true" : "false";
      navItem.dataset.pulse = active ? "true" : "false";
    }
  }

  private applyTargetSnapshot(
    target: WallTargetElements,
    entry: WallSnapshotEntry,
    anyWallActive: boolean,
  ): void {
    const messages = getMessages();
    const wallMessages = messages.wall.walls[entry.id];
    const targetChanged = target.root.dataset.wallId !== entry.id;
    if (targetChanged) {
      this.cancelStartChargeFill(target);
      this.cancelStopChargeFill(target);
      target.startBlockedUntilPointerLeaves = false;
    }

    target.root.hidden = false;
    target.root.setAttribute("aria-hidden", "false");
    target.root.setAttribute(
      HOVER_ANCHOR_TARGET_ID_ATTRIBUTE,
      `wall-${entry.id}`,
    );
    target.root.setAttribute(
      "aria-label",
      messages.wall.startAriaLabel(wallMessages.name),
    );
    target.stopButton.setAttribute(
      "aria-label",
      messages.wall.stopAriaLabel(wallMessages.name),
    );

    target.root.dataset.wallId = entry.id;
    target.root.dataset.wallStage = String(getWallOrdinal(entry.id));
    target.root.dataset.challengeAvailable = entry.challengeAvailable
      ? "true"
      : "false";
    target.root.dataset.active = entry.active ? "true" : "false";
    target.root.dataset.startable = !anyWallActive ? "true" : "false";
    target.root.dataset.otherActive = "false";

    target.nameEl.textContent = wallMessages.name;
    target.constraintEl.textContent = wallMessages.constraint;
    target.firstClearRewardEl.textContent = formatFirstClearRewardLabel(
      entry.firstClearReward,
      entry.firstClearRewardClaimed,
    );
    target.firstClearRewardEl.dataset.claimed = entry.firstClearRewardClaimed
      ? "true"
      : "false";
    target.rewardEl.textContent = `${messages.wall.rewardLabel}: ${wallMessages.reward}`;
    target.bestEl.textContent = formatBestClearLabel(entry.bestClearMs);
    const rewardCapReached = entry.rewardEffectiveCap?.reached === true;
    target.multiplierLabelEl.textContent = `${
      rewardCapReached
        ? messages.wall.currentMultiplierCapReachedLabel
        : messages.wall.currentMultiplierLabel
    }: `;
    target.multiplierRow.dataset.capReached = rewardCapReached
      ? "true"
      : "false";
    target.multiplierEl.textContent = formatWallRewardEffect(
      entry.rewardEffect,
    );

    target.stopButton.hidden = !entry.active;
    target.stopButton.setAttribute(
      "aria-hidden",
      entry.active ? "false" : "true",
    );

    if (entry.active) {
      target.root.classList.add("hover-idle-wall-card--active");
      target.root.classList.remove("hover-idle-wall-card--charging");
      this.cancelStartChargeFill(target);

      const elapsedLabel =
        entry.currentRunElapsedMs !== null
          ? formatDurationLabel(entry.currentRunElapsedMs)
          : null;

      target.stateEl.textContent =
        elapsedLabel !== null
          ? `${messages.wall.inProgressLabel}: ${elapsedLabel}`
          : messages.wall.inProgressLabel;
    } else {
      target.root.classList.remove("hover-idle-wall-card--active");
      this.cancelStopChargeFill(target);
      target.stateEl.textContent = messages.wall.inactive;
      if (anyWallActive) {
        this.cancelStartChargeFill(target);
      }
    }
  }

  private hideTarget(): void {
    this.cancelStartChargeFill(this.target);
    this.cancelStopChargeFill(this.target);
    this.target.root.hidden = true;
    this.target.root.setAttribute("aria-hidden", "true");
    this.target.root.dataset.wallId = "";
    this.target.root.dataset.challengeAvailable = "false";
  }

  private createNavItem(wallId: WallId, index: number): HTMLButtonElement {
    const messages = getMessages();
    const wallMessages = messages.wall.walls[wallId];
    const item = document.createElement("button");
    item.type = "button";
    item.className = "hover-idle-wall-nav-item";
    item.dataset.wallId = wallId;
    item.dataset.wallStage = String(index + 1);
    item.textContent = getWallRomanNumeral(index);
    item.setAttribute("aria-label", wallMessages.name);

    const select = (): void => {
      this.selectWallFromNav(wallId);
    };
    item.addEventListener("pointerenter", select);
    item.addEventListener("pointerdown", select);
    item.addEventListener("focus", select);
    item.addEventListener("click", select);
    this.cleanups.push(() => {
      item.removeEventListener("pointerenter", select);
      item.removeEventListener("pointerdown", select);
      item.removeEventListener("focus", select);
      item.removeEventListener("click", select);
    });
    return item;
  }

  private selectWallFromNav(wallId: WallId): void {
    const wall = this.lastWallSnapshot;
    if (wall === null) return;
    if (!this.canSelectWallFromNav(wall, wallId)) return;
    if (this.selectedWallId === wallId) return;

    this.cancelStartChargeFill(this.target);
    this.cancelStopChargeFill(this.target);
    this.target.startBlockedUntilPointerLeaves = false;
    this.selectedWallId = wallId;
    this.renderWallSnapshot(wall);
  }

  private canSelectWallFromNav(wall: WallSnapshot, wallId: WallId): boolean {
    if (wall.activeWallId !== null && wall.activeWallId !== wallId) {
      return false;
    }
    const entry = wall.walls.find((candidate) => candidate.id === wallId);
    return entry?.challengeAvailable ?? false;
  }

  private createTarget(): WallTargetElements {
    const messages = getMessages();
    const fill = document.createElement("div");
    fill.className = "hover-idle-wall-card-fill";
    fill.setAttribute("aria-hidden", "true");

    const root = document.createElement("div");
    root.className = "hover-idle-wall-card hover-idle-wall-target";
    root.tabIndex = 0;
    root.setAttribute("role", "button");
    root.hidden = true;

    const content = document.createElement("div");
    content.className = "hover-idle-wall-card-content";

    const stopButton = document.createElement("button");
    stopButton.type = "button";
    stopButton.className = "hover-idle-wall-card-stop";
    stopButton.hidden = true;
    stopButton.dataset.virtualPointer = "allow";
    stopButton.innerHTML = xLgIconSource;

    const nameEl = document.createElement("h3");
    nameEl.className = "hover-idle-wall-card-name";

    const constraintEl = document.createElement("p");
    constraintEl.className = "hover-idle-wall-card-constraint";

    const firstClearRewardEl = document.createElement("p");
    firstClearRewardEl.className = "hover-idle-wall-card-first-clear-reward";

    const rewardEl = document.createElement("p");
    rewardEl.className = "hover-idle-wall-card-reward";

    const multiplierRow = document.createElement("p");
    multiplierRow.className = "hover-idle-wall-card-multiplier";
    const multiplierLabelEl = document.createElement("span");
    multiplierLabelEl.className = "hover-idle-wall-card-multiplier-label";
    const multiplierEl = document.createElement("span");
    multiplierEl.className = "hover-idle-wall-card-multiplier-value";
    multiplierRow.append(multiplierLabelEl, multiplierEl);

    const bestRow = document.createElement("p");
    bestRow.className = "hover-idle-wall-card-best";
    const bestLabel = document.createElement("span");
    bestLabel.textContent = `${messages.wall.bestClearLabel}: `;
    const bestEl = document.createElement("span");
    bestEl.className = "hover-idle-wall-card-best-value";
    bestRow.append(bestLabel, bestEl);

    const stateRow = document.createElement("p");
    stateRow.className = "hover-idle-wall-card-state";
    const stateEl = document.createElement("span");
    stateEl.className = "hover-idle-wall-card-state-value";
    stateRow.append(stateEl);

    content.append(
      nameEl,
      constraintEl,
      firstClearRewardEl,
      rewardEl,
      multiplierRow,
      bestRow,
      stateRow,
    );
    root.append(fill, stopButton, content);

    const target: WallTargetElements = {
      root,
      fill,
      stopButton,
      nameEl,
      constraintEl,
      firstClearRewardEl,
      rewardEl,
      stateEl,
      multiplierRow,
      multiplierLabelEl,
      multiplierEl,
      bestEl,
      startIntent: null,
      stopIntent: null,
      startChargeController: null,
      stopChargeController: null,
      startBlockedUntilPointerLeaves: false,
      stopChargeDecaying: false,
      stopChargeDecayFromProgress: 0,
    };
    target.startChargeController = new HoverChargeExecutionController({
      getDurationMs: () => Math.max(0, this.getWallActionChargeDurationMs()),
      canExecute: () => {
        const wallId = this.selectedWallId;
        return wallId !== null && this.canStartChargeFill(wallId, target);
      },
      onProgress: (progress) => this.applyStartChargeProgress(target, progress),
      onExecute: () => {
        const wallId = this.selectedWallId;
        if (wallId !== null) {
          this.completeStartChargeFill(wallId, target);
        }
      },
    });
    target.stopChargeController = new HoverChargeExecutionController({
      getDurationMs: () => Math.max(0, this.getWallActionChargeDurationMs()),
      canExecute: () => {
        const wallId = this.selectedWallId;
        return wallId !== null && this.canStopChargeFill(wallId);
      },
      onProgress: (progress) => this.applyStopChargeProgress(target, progress),
      onExecute: () => {
        const wallId = this.selectedWallId;
        if (wallId !== null) {
          this.completeStopChargeFill(wallId, target);
        }
      },
    });
    target.startIntent = new HoverIntentController({
      target: root,
      pointerModel: this.pointerModel,
      onHoverChange: (isHovering) => {
        const wallId = this.selectedWallId;
        if (wallId === null) return;
        if (isHovering) {
          this.beginStartChargeFill(wallId, target);
        } else {
          target.startBlockedUntilPointerLeaves = false;
          this.releaseStartChargeFill(target);
        }
      },
    });
    target.stopIntent = new HoverIntentController({
      target: stopButton,
      pointerModel: this.pointerModel,
      onHoverChange: (isHovering) => {
        const wallId = this.selectedWallId;
        if (wallId === null) return;
        if (isHovering) {
          this.beginStopChargeFill(wallId, target);
        } else {
          this.releaseStopChargeFill(target);
        }
      },
    });
    const stopEventPropagation = (event: Event): void => {
      event.stopPropagation();
    };
    stopButton.addEventListener("pointerdown", stopEventPropagation);
    stopButton.addEventListener("click", stopEventPropagation);
    this.cleanups.push(() => {
      stopButton.removeEventListener("pointerdown", stopEventPropagation);
      stopButton.removeEventListener("click", stopEventPropagation);
    });

    return target;
  }

  private beginStartChargeFill(
    wallId: WallId,
    target: WallTargetElements,
  ): void {
    if (!this.canStartChargeFill(wallId, target)) return;
    target.root.classList.add("hover-idle-wall-card--charging");
    target.root.dataset.chargeAction = "start";
    target.startChargeController?.setHovered(true);
  }

  private completeStartChargeFill(
    wallId: WallId,
    target: WallTargetElements,
  ): void {
    const succeeded = this.onStartWall(wallId);
    if (!succeeded) {
      this.resetChargeFill(target);
    }
  }

  private cancelStartChargeFill(target: WallTargetElements): void {
    target.startChargeController?.cancel();
    if ((target.stopChargeController?.getProgress() ?? 0) > 0) return;
    this.resetChargeFill(target);
  }

  private releaseStartChargeFill(target: WallTargetElements): void {
    target.startChargeController?.setHovered(false);
    if ((target.startChargeController?.getProgress() ?? 0) > 0) return;
    if ((target.stopChargeController?.getProgress() ?? 0) > 0) return;
    this.resetChargeFill(target);
  }

  private beginStopChargeFill(
    wallId: WallId,
    target: WallTargetElements,
  ): void {
    if (!this.canStopChargeFill(wallId)) return;

    this.cancelStartChargeFill(target);
    target.stopChargeDecaying = false;
    target.root.classList.add("hover-idle-wall-card--charging");
    target.root.dataset.chargeAction = "stop";
    this.primeStopChargeFillOrigin(target);
    target.stopChargeController?.setHovered(true);
  }

  // The cancel fill's visual origin is the inverse of every other charge fill:
  // progress 0 means fully outer (scale 1), not the empty resting state. The
  // shared controller never emits onProgress for progress 0, so a fresh stop
  // hover must paint that outer origin itself before charging begins. Skip when
  // resuming a partially decayed charge so re-hover continues from its current
  // position instead of snapping back outward.
  private primeStopChargeFillOrigin(target: WallTargetElements): void {
    if ((target.stopChargeController?.getProgress() ?? 0) > 0) return;
    target.fill.style.transition = "none";
    target.fill.style.transform = "scale(1)";
    target.fill.style.opacity = "0.8";
    void target.fill.offsetHeight;
  }

  private completeStopChargeFill(
    wallId: WallId,
    target: WallTargetElements,
  ): void {
    const succeeded = this.onStopWall(wallId);
    if (succeeded) {
      target.startBlockedUntilPointerLeaves = true;
    } else {
      this.resetChargeFill(target);
    }
  }

  private cancelStopChargeFill(target: WallTargetElements): void {
    target.stopChargeController?.cancel();
    target.stopChargeDecaying = false;
    if ((target.startChargeController?.getProgress() ?? 0) > 0) return;
    this.resetChargeFill(target);
  }

  private releaseStopChargeFill(target: WallTargetElements): void {
    target.stopChargeController?.setHovered(false);
    const stopProgress = target.stopChargeController?.getProgress() ?? 0;
    if (stopProgress > 0) {
      target.stopChargeDecaying = true;
      target.stopChargeDecayFromProgress = stopProgress;
      return;
    }
    target.stopChargeDecaying = false;
    if ((target.startChargeController?.getProgress() ?? 0) > 0) return;
    this.resetChargeFill(target);
  }

  private canStartChargeFill(
    wallId: WallId,
    target: WallTargetElements,
  ): boolean {
    if (target.startBlockedUntilPointerLeaves) return false;
    const state = this.viewState.get(wallId);
    if (state === undefined) return false;
    if (!state.challengeAvailable) return false;
    return !state.active && !state.anyWallActive;
  }

  private canStopChargeFill(wallId: WallId): boolean {
    const state = this.viewState.get(wallId);
    return state !== undefined && state.active;
  }

  private applyStartChargeProgress(
    target: WallTargetElements,
    progress: number,
  ): void {
    if (progress <= 0) {
      if ((target.stopChargeController?.getProgress() ?? 0) <= 0) {
        this.resetChargeFill(target);
      }
      return;
    }
    target.root.classList.add("hover-idle-wall-card--charging");
    target.root.dataset.chargeAction = "start";
    target.fill.style.transition = "transform 80ms linear, opacity 80ms linear";
    target.fill.style.transform = `scale(${progress})`;
    target.fill.style.opacity = String(0.85 + progress * 0.15);
  }

  private applyStopChargeProgress(
    target: WallTargetElements,
    progress: number,
  ): void {
    if (progress <= 0) {
      target.stopChargeDecaying = false;
      if ((target.startChargeController?.getProgress() ?? 0) <= 0) {
        this.resetChargeFill(target);
      }
      return;
    }
    target.root.classList.add("hover-idle-wall-card--charging");
    target.root.dataset.chargeAction = "stop";
    const remaining = 1 - progress;
    // Scale tracks 1 - progress in both phases so a mid-decay re-hover resumes
    // seamlessly from the current position. While decaying, fade opacity toward
    // 0 as the charge drains so the fill settles invisibly at progress 0 instead
    // of re-inflating to the full primed origin and flashing on the idle reset.
    const opacity = target.stopChargeDecaying
      ? (0.12 + remaining * 0.68) *
        stopDecayFadeFactor(progress, target.stopChargeDecayFromProgress)
      : 0.12 + remaining * 0.68;
    target.fill.style.transition = "transform 80ms linear, opacity 80ms linear";
    target.fill.style.transform = `scale(${remaining})`;
    target.fill.style.opacity = String(opacity);
  }

  private resetChargeFill(target: WallTargetElements): void {
    target.root.classList.remove("hover-idle-wall-card--charging");
    delete target.root.dataset.chargeAction;
    target.fill.style.transition =
      "transform 200ms ease-out, opacity 200ms ease-out";
    target.fill.style.transform = "scale(0)";
    target.fill.style.opacity = "0";
  }

  dispose(): void {
    this.target.startChargeController?.dispose();
    this.target.startChargeController = null;
    this.target.stopChargeController?.dispose();
    this.target.stopChargeController = null;
    this.target.startIntent?.dispose();
    this.target.startIntent = null;
    this.target.stopIntent?.dispose();
    this.target.stopIntent = null;
    this.navItems.clear();
    this.viewState.clear();
    this.lastWallSnapshot = null;
    this.selectedWallId = null;
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups.length = 0;
    this.startStopChargeDurationButtons.length = 0;
    this.root.remove();
  }

  private createStartStopChargeDurationControl(
    setting: WallStartStopChargeDurationSetting,
  ): HTMLDivElement {
    const messages = getMessages();
    const wrapper = document.createElement("div");
    wrapper.className =
      "hover-idle-game-surface-segmented-control hover-idle-wall-start-stop-charge-duration";
    wrapper.setAttribute("role", "radiogroup");
    wrapper.setAttribute(
      "aria-label",
      messages.wall.startStopChargeDurationLabel,
    );

    const label = document.createElement("span");
    label.className =
      "hover-idle-game-surface-segmented-control-label hover-idle-wall-start-stop-charge-duration-label";
    label.textContent = `${messages.wall.startStopChargeDurationLabel}:`;
    wrapper.append(label);

    const segments = document.createElement("div");
    segments.className =
      "hover-idle-game-surface-segmented-control-options hover-idle-wall-start-stop-charge-duration-segments";

    for (const optionMs of setting.options) {
      const button = document.createElement("button");
      button.type = "button";
      button.className =
        "hover-idle-game-surface-segmented-control-option hover-idle-wall-start-stop-charge-duration-segment";
      button.dataset.virtualPointer = "allow";
      button.setAttribute("role", "radio");
      button.textContent = messages.wall.startStopChargeDurationOption(
        String(optionMs / 1000),
      );
      button.dataset.ms = String(optionMs);
      const handler = (): void => {
        setting.onChange(optionMs);
      };
      button.addEventListener("pointerenter", handler);
      button.addEventListener("pointerdown", handler);
      button.addEventListener("focus", handler);
      button.addEventListener("click", handler);
      this.cleanups.push(() => {
        button.removeEventListener("pointerenter", handler);
        button.removeEventListener("pointerdown", handler);
        button.removeEventListener("focus", handler);
        button.removeEventListener("click", handler);
      });
      this.startStopChargeDurationButtons.push(button);
      segments.append(button);
    }
    wrapper.append(segments);
    return wrapper;
  }

  private syncStartStopChargeDurationControl(): void {
    if (this.startStopChargeDurationButtons.length === 0) return;
    const currentMs = this.getWallActionChargeDurationMs();
    for (const button of this.startStopChargeDurationButtons) {
      const active = Number(button.dataset.ms) === currentMs;
      button.dataset.active = active ? "true" : "false";
      button.setAttribute("aria-checked", active ? "true" : "false");
    }
  }
}

function getKnownWallIds(): readonly WallId[] {
  return WALL_DEFINITIONS.map((wall) => wall.id);
}

function stopDecayFadeFactor(progress: number, fromProgress: number): number {
  if (fromProgress <= 0) return 0;
  return Math.min(1, progress / fromProgress);
}

function createWallEntryMap(
  wall: WallSnapshot,
): ReadonlyMap<WallId, WallSnapshotEntry> {
  return new Map(wall.walls.map((entry) => [entry.id, entry]));
}

function getVisibleWallIds(wall: WallSnapshot): readonly WallId[] {
  const entriesById = createWallEntryMap(wall);
  return getKnownWallIds().filter(
    (wallId) => entriesById.get(wallId)?.challengeAvailable ?? false,
  );
}

function getWallOrdinal(wallId: WallId): number {
  const index = getKnownWallIds().indexOf(wallId);
  return index >= 0 ? index + 1 : 1;
}

function getWallRomanNumeral(index: number): string {
  return WALL_ROMAN_NUMERALS[index] ?? String(index + 1);
}

function formatBestClearLabel(bestClearMs: number | null): string {
  const messages = getMessages();
  if (bestClearMs === null) return messages.wall.notClearedYet;
  return (
    formatOHovRecordDurationMs(
      bestClearMs,
      messages.common.formatMilliseconds,
      formatDurationLabel,
    ) ?? messages.wall.notClearedYet
  );
}

function formatFirstClearRewardLabel(
  reward: WallFirstClearReward,
  claimed: boolean,
): string {
  const messages = getMessages();
  if (reward.kind === "ohov-mp-control-unlock") {
    return claimed
      ? messages.wall.firstClearRewardClaimedTextLabel(
          messages.wall.firstClearRewards.ohovMpControlUnlock,
        )
      : messages.wall.firstClearRewardTextLabel(
          messages.wall.firstClearRewards.ohovMpControlUnlock,
        );
  }
  if (reward.kind === "nhov-first-branch-selection-unlock") {
    return claimed
      ? messages.wall.firstClearRewardClaimedTextLabel(
          messages.wall.firstClearRewards.nhovFirstBranchSelectionUnlock,
        )
      : messages.wall.firstClearRewardTextLabel(
          messages.wall.firstClearRewards.nhovFirstBranchSelectionUnlock,
        );
  }
  if (reward.kind === "amplifier-la-da-availability-unlock") {
    return claimed
      ? messages.wall.firstClearRewardClaimedTextLabel(
          messages.wall.firstClearRewards.amplifierLaDaAvailabilityUnlock,
        )
      : messages.wall.firstClearRewardTextLabel(
          messages.wall.firstClearRewards.amplifierLaDaAvailabilityUnlock,
        );
  }
  const formatted = formatNumber(reward.amount);
  return claimed
    ? messages.wall.firstClearRewardClaimedLabel(formatted)
    : messages.wall.firstClearRewardLabel(formatted);
}

function formatNumber(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2);
}

function formatDurationLabel(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const paddedSeconds = seconds.toString().padStart(2, "0");
  return `${minutes}:${paddedSeconds}`;
}
